/**
 * Telemetry data models for Core Web Vitals, errors, and custom metrics.
 * Kept flat and simple for fast serialization and minimal bundle size.
 */

/**
 * Telemetry event types for performance and error monitoring
 */
export enum TelemetryEventType {
  WEB_VITAL_LCP = 'web_vital_lcp',
  WEB_VITAL_FCP = 'web_vital_fcp',
  WEB_VITAL_CLS = 'web_vital_cls',
  WEB_VITAL_INP = 'web_vital_inp',
  WEB_VITAL_TTFB = 'web_vital_ttfb',
  ERROR = 'error',
  CUSTOM = 'custom',
  PAGE_LOAD = 'page_load',
  REPORTING = 'reporting',
  USER_CONTEXT = 'user_context',
}

/**
 * Rating for Web Vitals metrics based on web.dev thresholds
 */
export type MetricRating = 'good' | 'needs-improvement' | 'poor';

/**
 * Web Vital metric types (FID removed - deprecated in favor of INP)
 */
export type WebVitalType = 'LCP' | 'CLS' | 'INP' | 'TTFB' | 'FCP';

/**
 * Web Vital metric data
 */
export interface WebVitalMetric {
  name: WebVitalType;
  value: number;
  rating: MetricRating;
  timestamp: number;
  // Attribution data for debugging
  element?: string; // CSS selector or element description
  load_state?: 'loading' | 'dom_interactive' | 'dom_content_loaded' | 'complete';
}

/**
 * Breadcrumb types for tracking user actions
 */
export type BreadcrumbType = 'click' | 'navigation';

/**
 * Breadcrumb data for error context
 */
export interface Breadcrumb {
  type: BreadcrumbType;
  timestamp: number;
  data: string;
}

/**
 * Error event data
 */
export interface ErrorEvent {
  message: string;
  stack?: string;
  url: string;
  line?: number;
  column?: number;
  is_third_party: boolean;
  timestamp: number;
  user_agent: string;
  // Navigation type for context
  navigation_type?: 'navigate' | 'reload' | 'back_forward' | 'prerender';
}

/**
 * Custom metric data
 */
export interface CustomMetric {
  name: string;
  value: number;
  timestamp: number;
}

/**
 * Page load metric data
 */
export interface PageLoadMetric {
  load_time: number; // Full page load time (load event)
  dom_content_loaded_time: number; // DOMContentLoaded time
  timestamp: number;
}

/**
 * Browser reporting event data from Reporting API
 */
export interface ReportingEvent {
  type: string; // deprecation, intervention, crash, csp-violation, etc.
  url: string;
  timestamp: number;
  body: Record<string, unknown>; // Report body varies by type
}

/**
 * User context data - device and environment information
 * Includes both raw and bucketed/rounded values for flexibility
 */
export interface UserContextEvent {
  timestamp: number;
  // Device
  cpu?: number; // Actual value from navigator.hardwareConcurrency
  memory?: number; // Device memory in GB (Chrome/Edge only)
  max_touch_points?: number;
  platform: 'desktop' | 'mobile' | 'tablet';
  input_touch: boolean;
  input_pointer: 'coarse' | 'fine' | 'none';
  input_hover: boolean;
  browser_name: string;
  browser_version: string;
  is_web_view: boolean;
  os_name: string;
  os_version?: string;
  // Capabilities
  has_service_worker: boolean;
  service_worker_status?: 'unsupported' | 'none' | 'installing' | 'waiting' | 'active';
  has_cookies: boolean;
  has_chips: boolean;
  has_local_storage: boolean;
  has_session_storage: boolean;
  has_indexed_db: boolean;
  // User Preferences
  language: string;
  languages: string[];
  timezone: string;
  direction?: 'ltr' | 'rtl';
  color_scheme?: 'light' | 'dark' | 'no-preference';
  prefers_reduced_motion: boolean;
  prefers_reduced_data: boolean;
  prefers_contrast?: 'no-preference' | 'more' | 'less';
  // Storage quota (flattened)
  storage_usage?: number;
  storage_quota?: number;
  storage_usage_percent?: number;
  // Media capabilities (flattened)
  h264?: boolean;
  vp9?: boolean;
  av1?: boolean;
  // Permissions (flattened)
  notifications_permission?: 'granted' | 'denied' | 'prompt';
  geolocation_permission?: 'granted' | 'denied' | 'prompt';
  // Display
  screen_width: number; // Bucketed to common resolutions
  screen_height: number; // Bucketed to common resolutions
  device_pixel_ratio: number; // Actual window.devicePixelRatio
  orientation: 'portrait' | 'landscape';
  // Viewport
  viewport_width: number; // Actual window.innerWidth
  viewport_height: number; // Actual window.innerHeight
  visual_scale?: number; // Pinch-zoom scale
  is_iframe: boolean;
  // Network
  effective_type?: '2g' | '3g' | '4g' | 'slow-2g'; // From Network Information API or estimated
  downlink_mbps?: number; // Actual downlink in Mbps
  rtt_ms?: number; // Actual RTT in milliseconds
  save_data_enabled: boolean;
  // Custom context data (flattened with context_ prefix)
  [key: `context_${string}`]: unknown;
  // Frame Context
  ancestor_origins: string[] | null;
}

/**
 * Base telemetry event
 */
export interface TelemetryEvent {
  type: TelemetryEventType;
  data: WebVitalMetric | ErrorEvent | CustomMetric | PageLoadMetric | ReportingEvent | UserContextEvent;
}

/**
 * Payload sent to tracking service
 */
export interface TelemetryPayload {
  events: TelemetryEvent[];
  sessionId: string;
  timestamp: number;
}

/**
 * Queued event for offline storage
 */
export interface QueuedEvent {
  id?: number;
  payload: TelemetryPayload;
  timestamp: number;
}

/**
 * Feature detection flags
 */
export interface FeatureFlags {
  hasPerformanceObserver: boolean;
  hasSessionStorage: boolean;
  hasIndexedDB: boolean;
  hasPerformanceMark: boolean;
  hasVisibilityAPI: boolean;
}

/**
 * Telemetry service configuration
 */
export interface TelemetryConfig {
  /** URL endpoint for sending telemetry data */
  trackingServiceUrl: string;
  /** Patterns to identify app-origin errors (optional) */
  appOriginPatterns?: string[];
  /** Maximum events to buffer before forcing flush (default: 100) */
  maxBufferSize?: number;
  /** Enable console logging for debugging (default: false) */
  enabledLogging?: boolean;
  /** Additional custom data to include in user-context event */
  customContextData?: Record<string, unknown>;
  /** Enable Web Vitals tracking (default: false) */
  enableWebVitals?: boolean;
  /** Enable error tracking (default: false) */
  enableErrorTracking?: boolean;
  /** Enable browser reporting tracker (default: false) */
  enableReporting?: boolean;
  /** Enable user context tracking (default: false) */
  enableUserContext?: boolean;
}
